import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { IoVolumeMute, IoVolumeHigh } from "react-icons/io5";
// Components
import Button from "../CustomButton";
// Styles
import styles from './style.module.scss';

interface VolumeControlProps {
  volume: number;
  onChange: (volume: number) => void;
}

const VolumeControl: React.FC<VolumeControlProps> = ({ volume, onChange }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position dropdown above or below
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 1000,
      });
      setTimeout(() => {
        if (dropdownRef.current) {
          const dropdownHeight = dropdownRef.current.offsetHeight;
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            setDropdownStyle({
              position: 'absolute',
              top: rect.top + window.scrollY - dropdownHeight,
              left: rect.left + window.scrollX,
              width: rect.width,
              zIndex: 1000,
            });
          } else {
            setDropdownStyle({
              position: 'absolute',
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
              zIndex: 1000,
            });
          }
        }
      }, 0);
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const renderIcon = () => {
    if (volume === 0) {
      return <IoVolumeMute />;
    }
    return <IoVolumeHigh />;
  }

  return (
    <div className={styles.volumeControl} ref={buttonRef} style={{ display: 'inline-block' }}>
      <Button variant="text" onClick={() => setOpen(!open)}>
        {renderIcon()}
      </Button>
      {open && typeof window !== 'undefined' && createPortal(
        <div className={styles.dropdown} ref={dropdownRef} style={dropdownStyle}>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={volume * 100}
            onChange={e => onChange(Number(e.target.value) / 100)}
            className={styles.slider}
          />
        </div>,
        document.body
      )}
    </div>
  )
}

export default VolumeControl;
